import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { asPerformanceDate, asTimeISO } from 'utils/date-formatter';
import { Link } from 'components/UI/Link/Link';
import { ReactComponent as SeatIcon } from 'assets/icons/svgs/icon-seat-2.svg';
import { toAudString } from 'utils/currency';
import { RemoveSeatsPrompt } from './Prompts/RemoveSeatsPrompt';

export const ConfirmSeats = (props) => {
  const {
    Order: {
      OrderTotal,
      Cart: { PerformanceTickets, TotalItems, Fees },
    },
    OrderDataLayer: {
      PerformanceTickets: PerformanceTicketsDataLayer,
    },
    urlTestParam,
  } = props;

  const [removeEvent, setRemoveEvent] = useState(null);
  const [currentPerformanceData, setCurrentPerformanceData] = useState(null);

  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const performanceIdNumber = urlTestParam || urlParams.get('perf_no');

    const currentPerformanceIndex = PerformanceTickets.findIndex(
      (currentTicket) => currentTicket.PerformanceId === parseInt(performanceIdNumber, 10)
    );
    if (currentPerformanceIndex > -1) {
      const currentPerformance = {
        ...PerformanceTickets[currentPerformanceIndex],
        ...PerformanceTicketsDataLayer[currentPerformanceIndex]
      };
      setCurrentPerformanceData(currentPerformance);
    }
  }, [PerformanceTickets]);

  const showPrompt = (event) => {
    setRemoveEvent({ ...currentPerformanceData, ...event });
  };

  const handleRequestClose = () => {
    setRemoveEvent(null);
  };

  const showDateTimeMessage = currentPerformanceData?.ShowDateTime && (
    <p className="lead">
      <strong>
        The following seats on {asPerformanceDate(currentPerformanceData.IsoPerformanceDate)} at{' '}
        <br className="visible-xs" />
        {asTimeISO(currentPerformanceData.IsoPerformanceDate)} have been added to your cart:
      </strong>
    </p>
  );

  const showDateOnlyMessage = currentPerformanceData?.ShowDateOnly && (
    <p className="lead">
      <strong>
        The following seats on {asPerformanceDate(currentPerformanceData.IsoPerformanceDate)} have been
        added to your cart:
      </strong>
    </p>
  );

  const hideDateTimeMessage = currentPerformanceData?.HideDateTime && (
    <p className="lead">
      <strong>The following seats have been added to your cart:</strong>
    </p>
  );

  return (
    currentPerformanceData && (
      <div className="container">
        <RemoveSeatsPrompt
          active={!!removeEvent}
          event={removeEvent}
          onRequestClose={handleRequestClose}
          stepName="Confirm Seats"
        />

        {showDateTimeMessage}
        {showDateOnlyMessage}
        {hideDateTimeMessage}

        <table className="table-css-responsive table-fullwidth cart-table cart-table-seat-review">
          <thead>
            <tr>
              <th>Seat</th>
              <th>Section</th>
              <th>Type</th>
              {currentPerformanceData.HasDiscountedTickets && (
                <th className="cell-price">Original price</th>
              )}
              <th
                className="cell-price"
                colSpan={currentPerformanceData.HasDiscountedTickets ? 1 : 2}
              >
                Price
              </th>
            </tr>
          </thead>
          <tbody>
            {currentPerformanceData.TicketedItems?.map((lineItem, lineItemIndex) => (
              <React.Fragment key={lineItemIndex}>
                {lineItem.SubLineItems.map((individualTicket, individualTicketIndex) => (
                  <tr className="row-seat-info" key={individualTicketIndex}>
                    <td className="seat-info">
                      {lineItem.IsSeated
                        ? individualTicket.SeatRow + individualTicket.SeatNumber
                        : ''}
                    </td>
                    <td className="seat-info">{individualTicket.SectionShortDescription}</td>
                    {individualTicket.SeatTypeAliasDescription && (
                      <td className="seat-info">{individualTicket.SeatTypeAliasDescription}</td>
                    )}
                    {!individualTicket.SeatTypeAliasDescription && (
                      <td className="seat-info">{individualTicket.SeatTypeDescription}</td>
                    )}
                    {currentPerformanceData.HasDiscountedTickets && (
                      <td className="seat-info cell-price">
                        {individualTicket.OrignalAmount > individualTicket.AmountDue && (
                          <span className="price-old">
                            {toAudString(individualTicket.OrignalAmount)}
                          </span>
                        )}
                        {individualTicket.OrignalAmount <= individualTicket.AmountDue && (
                          <span>&nbsp;</span>
                        )}
                      </td>
                    )}
                    <td
                      className="seat-info cell-price"
                      colSpan={currentPerformanceData.HasDiscountedTickets ? 1 : 2}
                    >
                      {toAudString(individualTicket.AmountDue)}
                    </td>
                  </tr>
                ))}

                <tr>
                  {(lineItem.ChangeSeatsURL || lineItem.ShowChangeSeats) && (
                    <td>
                      <Link
                        startIcon={<SeatIcon />}
                        href={lineItem.ChangeSeatsURL}
                        extendClass="link-bold cart-remove"
                        variant="secondary"
                      >
                        Change your seats?
                      </Link>
                    </td>
                  )}
                  <td colSpan="5" className="seat-info cell-remove">
                    <Link
                      href="#"
                      onClick={() =>
                        showPrompt({ ...lineItem, EventUrl: currentPerformanceData?.EventUrl })
                      }
                      extendClass="link-bold cart-remove"
                      variant="secondary"
                    >
                      Remove seats
                    </Link>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>

          <tbody>
            {currentPerformanceData.OriginalPerformanceTotal > 0 &&
              currentPerformanceData.OriginalPerformanceTotal >
                currentPerformanceData.PerformanceTotal && (
                <tr className="row-cart-subtotal">
                  <td colSpan={currentPerformanceData.HasDiscountedTickets ? 5 : 4}>
                    <div className="row">
                      <div className="col-sm-12 cell-price text-right">
                        <div className="row-heading">Original Subtotal</div>
                        <span className="price-old">
                          {toAudString(currentPerformanceData.OriginalPerformanceTotal)}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            <tr className="row-cart-subtotal">
              <td colSpan={currentPerformanceData.HasDiscountedTickets ? 5 : 4}>
                <div className="row">
                  <div className="col-sm-6">&nbsp;</div>
                  <div className="col-sm-4 text-right">
                    <div className="row-heading">Subtotal</div>
                  </div>
                  <div className="col-sm-2 cell-price text-right">
                    {toAudString(currentPerformanceData.PerformanceTotal)}
                  </div>
                </div>
              </td>
            </tr>
            {TotalItems === 1 ||
              (currentPerformanceData.HasDiscountedTickets && (
                <tr className="row-cart-subtotal">
                  <td colSpan={currentPerformanceData.HasDiscountedTickets ? 5 : 4}>
                    {Fees?.map((fee, feeIndex) => (
                      <React.Fragment key={feeIndex}>
                        <div className="row">
                          <div className="col-sm-6">
                            {fee.Description === 'Online Service Charge' && (
                              <small className="cart-disclaim">
                                Any additional postage charges that may apply will be added when you
                                select your delivery method.
                              </small>
                            )}
                          </div>
                          <div className="col-sm-4 text-right">
                            <div className="row-heading">{fee.Description}</div>
                          </div>
                          <div className="col-sm-2 cell-price text-right">
                            {toAudString(fee.Amount)}
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </td>
                </tr>
              ))}
            <tr className="row-cart-subtotal">
              <td colSpan={currentPerformanceData.HasDiscountedTickets ? 5 : 4}>
                <div className="row">
                  <div className="col-sm-6">&nbsp;</div>
                  <div className="col-sm-4 text-right">
                    <div className="row-heading">Cart Total</div>
                  </div>
                  <div className="col-sm-2 cell-price text-right">{toAudString(OrderTotal)}</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <small>Online orders may be subject to a Service Charge from $8.95 per order.</small>
        </p>
      </div>
    )
  );
};

ConfirmSeats.propTypes = {
  Order: PropTypes.object,
  OrderDataLayer: PropTypes.object,
  urlTestParam: PropTypes.number,
};

export const ConfirmSeatsSC = (props) => {
  const { sitecoreData } = props;
  return <ConfirmSeats {...sitecoreData} />;
};

ConfirmSeatsSC.propTypes = {
  sitecoreData: PropTypes.object,
};
